import { AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import environment from '../../../../environments/environment';

import { AuthService, LoginResponse } from '../../../services/auth/auth.service';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { ScrollService } from '../../../services/scroll.service';
import { ValidationMessageService } from '../../../services/validation-message.service';

import { LoginRequest } from '../../../services/auth/login-request.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GrowlerService } from '../../../services/growler.service';
import { MatDialog } from '@angular/material/dialog';
import { Confirm2faCodeComponent, ConfirmTwoFactorInput, ConfirmTwoFactorOutput } from '../../app-layout/settings/user/security/confirm-2fa-code/confirm-2fa-code.component';

@UntilDestroy()
@Component({
    selector: 'login-box',
    styleUrls: [
        '../auth-layout.scss',
    ],
    templateUrl: `./login.component.html`,
})
export class LoginComponent implements OnInit, AfterViewInit
{
    @ViewChild('emailInput')
    emailInput: ElementRef;

    // Form model variables.
    isActive = false;
    isSubmitting = false;
    targetUri: string;
    isForwarding = false;

    // Form control objects / validators.
    model: FormGroup;
    private loginOrgId = null;

    constructor(
            private _activatedRoute: ActivatedRoute,
            private _builder: FormBuilder,
            private _router: Router,
            private _authService: AuthService,
            private _scrollService: ScrollService,
            private _renderer: Renderer2,
            private _validationMessageService: ValidationMessageService,
            private _growler: GrowlerService,
            private _dialog: MatDialog,
    ) {
    }

    ngOnInit(): void {
        if (LegFiJwtService.raw() === 'false') {
            LegFiJwtService.clear();
        }

        this.model = this._builder.group({
            email: [''],
            password: [''],
        });

        this.isActive = true;

        // Scroll to top on view load.
        this._scrollService.scrollToTop();

        this._activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe(
                params => {
                    this.targetUri = params['uri'] || null;
                    this.loginOrgId = params['orgId'];

                    if (params['nonce'] && params['orgId']) {
                        this.isSubmitting = true;
                        this._authService.nonceLogin(params['nonce'], params['orgId']).subscribe(
                                (data: any) => {
                                    // Store the user's JWT in local storage.
                                    if (data.hasOwnProperty('token')) {
                                        LegFiJwtService.store(data['token']);
                                    }

                                    if (typeof this.targetUri !== 'undefined' && this.targetUri !== null && this.targetUri.length > 0) {
                                        // noinspection JSIgnoredPromiseFromCall
                                        this._router.navigateByUrl(decodeURIComponent(this.targetUri));
                                    } else {
                                        // if this is an owner but not admin redirect to their unit
                                        const jwt = LegFiJwtService.read();
                                        if (jwt.unitId && !jwt.admin) {
                                            // noinspection JSIgnoredPromiseFromCall
                                            this._router.navigate(['/app/unit/detail/' + jwt.unitId]);
                                        } else {
                                            // noinspection JSIgnoredPromiseFromCall
                                            this._router.navigate(['/app/owner/dashboard']);
                                        }

                                    }
                                },
                                (response) => {
                                    this.isSubmitting = false;
                                    this._growler.error('Error', this._validationMessageService.extractResponseErrorText(response)
                                            || 'This link has expired, please log in to continue to your targeted page.');
                                },
                        );
                    } else {
                        const alreadyLoggedInJwt = LegFiJwtService.read();
                        if (alreadyLoggedInJwt) {
                            this.isForwarding = true;

                            const redirect = () => {
                                if (typeof this.targetUri !== 'undefined' && this.targetUri !== null && this.targetUri.length > 0) {
                                    // noinspection JSIgnoredPromiseFromCall
                                    this._router.navigateByUrl(decodeURIComponent(this.targetUri));
                                    return;
                                }

                                if (alreadyLoggedInJwt.admin || alreadyLoggedInJwt.superUser) {
                                    this._router.navigate(['/app/organization/dashboard']);
                                    return;
                                }

                                if (alreadyLoggedInJwt.unitId) {
                                    this._router.navigate(['/app/unit/detail/' + alreadyLoggedInJwt.unitId]);
                                    return;
                                }

                                this._router.navigate(['/app/owner/dashboard']);
                            };

                            if (params.hasOwnProperty('orgId')) {
                                if (alreadyLoggedInJwt.orgId !== params['orgId']) {
                                    this._authService.switchAccounts(params['orgId'], {siteId: environment.SiteId})
                                            .subscribe(
                                                    () => {
                                                        redirect();
                                                    },
                                                    () => {
                                                        this._growler.error('Error Logging In', 'There was an error switching organizations. Please login manually.');
                                                    },
                                            );
                                }
                            } else {
                                redirect();
                            }


                            return;
                        }
                    }
                },
        );
    }

    ngAfterViewInit() {
        if (this.emailInput) {
            this._renderer.selectRootElement(this.emailInput.nativeElement).focus();
        }
    }

    submitByEnter(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            this.onSubmit();
        }
    }

    onSubmit(): void {
        if (this.isSubmitting) {
            return;
        }

        if (this.model.invalid) {
            this._growler.error('Error', 'Please check all required fields.');
            return;
        }

        this.isSubmitting = true;

        const loginRequest: LoginRequest = <LoginRequest>this.model.value;
        loginRequest.siteId = environment.SiteId;

        this.attemptLogin(loginRequest);
    }

    private successfulLogin(data) {
        LegFiJwtService.store(data['token']);

        const redirect = () => {
            if (typeof this.targetUri !== 'undefined' && this.targetUri !== null && this.targetUri.length > 0) {
                // noinspection JSIgnoredPromiseFromCall
                this._router.navigateByUrl(decodeURIComponent(this.targetUri));
            } else {
                const jwt = LegFiJwtService.read();

                if (jwt.admin || jwt.superUser) {
                    return this._router.navigate(['/app/organization/dashboard']);
                }

                if (jwt.unitId) {
                    return this._router.navigate(['/app/unit/detail/' + jwt.unitId]);
                }

                this._router.navigate(['/app/owner/dashboard']);
            }
        };

        const payload = LegFiJwtService.read();
        if (this.loginOrgId && this.loginOrgId !== payload.orgId) {
            this._authService
                    .switchAccounts(this.loginOrgId, {siteId: environment.SiteId})
                    .subscribe(
                            () => {
                                redirect();
                            },
                            () => {
                                this._growler.error('Error Switching Organizations', 'There was an error switching organizations upon login. Please try again.');
                                redirect();
                            },
                    );
        } else {
            redirect();
        }
    };

    private attemptLogin(loginRequest: LoginRequest) {
        this._authService.login(loginRequest).subscribe({
            next: (data: LoginResponse) => {
                // Store the user's JWT in local storage.
                if (data.hasOwnProperty('token')) {
                    this.successfulLogin(data);
                } else if (data.hasOwnProperty('twoFactor')) {
                    const twoFactorInput = <ConfirmTwoFactorInput>{
                        userId: data.twoFactor.userId,
                    };
                    if (data.twoFactor.sms && !data.twoFactor.google) {
                        twoFactorInput.type = 'sms';
                        twoFactorInput.canGoBack = false;
                    } else if (data.twoFactor.google && !data.twoFactor.sms) {
                        twoFactorInput.type = 'google';
                        twoFactorInput.canGoBack = false;
                    }
                    this._dialog.open(Confirm2faCodeComponent, {
                        width: '555px',
                        data: twoFactorInput,
                    }).afterClosed().subscribe({
                        next: (result: ConfirmTwoFactorOutput) => {
                            if (result?.success) {
                                loginRequest.twoFactorKey = result.token;
                                loginRequest.twoFactorType = result.type;
                                this.attemptLogin(loginRequest);
                            } else {
                                this._growler.error('Error', 'Two factor verification is required to login with this account.');
                                this.isSubmitting = false;
                            }
                        },
                    });
                }
            },
            error: (response) => {
                this._growler.error('Error', this._validationMessageService.extractResponseErrorText(response)
                        || 'We were unable to log you in.');

                this.isSubmitting = false;
            },
        });
    }
}
